import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Download, RefreshCw } from 'lucide-react';

import { useMyLeaveApi } from './config/useMyLeaveApi';
import { parseTableParams, formatDate } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import DateTimePicker from '../../components/ui/DateTimePicker';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import PictureUpload from '../../components/ui/PictureUpload';
import api from '../../services/api';
import { presignAndUploadFile } from '../../services/storageService';
import {
  getMyReconciliation, downloadMyReconciliationPdf, regenerateMyReconciliation,
} from '../../services/leaveReconciliationService';

const Container = styled.div``;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding-top: 40px;
  overflow-y: auto;
`;

const DialogBox = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 560px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
  margin-bottom: 40px;
`;

const DialogTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 20px;
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const StepDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active, theme }) => $active ? theme.colors.primary.main : '#E0E4E8'};
  transition: background 0.2s;
`;

const VariationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
  margin-bottom: 20px;
`;

const VariationItem = styled.div`
  padding: 14px 16px;
  border: 1.5px solid ${({ $selected, theme }) => $selected ? theme.colors.primary.main : '#E0E4E8'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $selected }) => $selected ? 'rgba(2, 145, 239, 0.04)' : '#fff'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const VariationName = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const VariationDesc = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const ValidationResult = styled.div`
  background: rgba(2, 145, 239, 0.04);
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
`;

const ValidationRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 4px 0;
`;

const ValidationLabel = styled.span`
  font-weight: 600;
  color: #6b7280;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const InfoNote = styled.div`
  background: rgba(2, 145, 239, 0.06);
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DocumentUploadWrap = styled.div`
  margin-bottom: 20px;
`;

const ReconciliationToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReconciliationYearBox = styled.div`
  width: 100px;
`;

const ReconciliationBadge = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 4px;
  white-space: nowrap;
  background: ${({ $delivered }) => ($delivered ? '#DCFCE7' : '#FEF3C7')};
  color: ${({ $delivered }) => ($delivered ? '#166534' : '#92400E')};
`;

const currentYear = new Date().getFullYear();
// Son 6 yıl (bugünkü yıl dahil) — LeaveReconciliationsPage.YEAR_OPTIONS ile AYNI aralık.
const RECONCILIATION_YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const y = currentYear - 5 + i;
  return { id: y, name: String(y) };
});

// Toplu bildirim e-postasındaki "Formu Görüntüle" bağlantısı `?reconciliationYear=YYYY`
// taşır (LeaveReconciliationBulkProcessor) — sayfa o yılın formuyla açılır. Seçici
// aralığı dışındaki ya da bozuk bir değer yok sayılır (içinde bulunulan yıl).
export function initialReconciliationYear(search) {
  const year = Number(new URLSearchParams(search || '').get('reconciliationYear'));
  return RECONCILIATION_YEAR_OPTIONS.some((o) => o.id === year) ? year : currentYear;
}

export default function MyLeavesPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [cancelConfirm, setCancelConfirm] = useState({ isOpen: false, id: null });
  const [canceling, setCanceling] = useState(false);

  // İzin mutabakat formu — kendi kaydım (Görev 11 koordinasyon boşluğu turu).
  const [reconciliationYear, setReconciliationYear] = useState(
    () => initialReconciliationYear(location.search),
  );
  const [reconciliationStatus, setReconciliationStatus] = useState(null);
  const [downloadingReconciliation, setDownloadingReconciliation] = useState(false);
  // Bulgu G — dökümü bilerek tazeleme. Onay ZORUNLU: form yıl içinde bir kez
  // üretildiğinde İÇERİĞİ DONAR; tazeleme o donmuş kümeyi değiştirir.
  const [regenerateConfirm, setRegenerateConfirm] = useState(false);
  const [regeneratingReconciliation, setRegeneratingReconciliation] = useState(false);

  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [variations, setVariations] = useState([]);
  const [selectedVariationId, setSelectedVariationId] = useState(null);
  const [leaveStart, setLeaveStart] = useState(null);
  const [leaveEnd, setLeaveEnd] = useState(null);
  const [afternoonExit, setAfternoonExit] = useState(false);
  const [afternoonEntry, setAfternoonEntry] = useState(false);
  const [userNotes, setUserNotes] = useState('');
  const [validationData, setValidationData] = useState(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentError, setDocumentError] = useState(null);

  const { columns: baseColumns, data, fetching, pagination, cancel, refresh } = useMyLeaveApi(queryParams);

  // Fetch variations on dialog open
  useEffect(() => {
    if (dialogOpen && step === 1) {
      api.get('/v1/leave-variations')
        .then((res) => { const r = res.data?.data; setVariations(Array.isArray(r) ? r : (r?.data || [])); })
        .catch(() => {});
    }
  }, [dialogOpen, step]);

  // Yıl sonu izin mutabakat formu — kayıt hiç üretilmemişse backend 404 döner
  // (`leaveReconciliation.notFound`); bu bir hata DEĞİL, "henüz oluşturulmadı"
  // durumudur (bkz. leaveReconciliationService.getMyReconciliation yorumu).
  // res.data?.status GERÇEK sinyaldir — false ise rozet "Teslim Edilmedi"
  // kalır, buton yine aktiftir (indirme kaydı kendisi açar).
  useEffect(() => {
    let cancelled = false;
    getMyReconciliation(reconciliationYear).then((res) => {
      if (cancelled) return;
      setReconciliationStatus(res.data?.status ? (res.data?.data?.status || null) : null);
    });
    return () => { cancelled = true; };
  }, [reconciliationYear]);

  const handleNewButtonClick = useCallback(() => {
    setStep(1);
    setSelectedVariationId(null);
    setLeaveStart(null);
    setLeaveEnd(null);
    setAfternoonExit(false);
    setAfternoonEntry(false);
    setUserNotes('');
    setValidationData(null);
    setDocumentFile(null);
    setDocumentError(null);
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback((row) => {
    setCancelConfirm({ isOpen: true, id: row.id });
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    setCanceling(true);
    await cancel(cancelConfirm.id);
    setCancelConfirm({ isOpen: false, id: null });
    setCanceling(false);
  }, [cancel, cancelConfirm.id]);

  const handleRowClick = useCallback((row) => {
    
    history.push(`/leaves/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const selectedVariation = useMemo(() =>
    variations.find((v) => v.id === selectedVariationId),
  [variations, selectedVariationId]);

  const handleStep2Next = useCallback(async () => {
    if (!leaveStart || !leaveEnd) return;
    setValidating(true);

    let effectiveStart = leaveStart;
    let effectiveEnd = leaveEnd;
    const isHourly = selectedVariation?.isHourly || false;

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await api.post('/v1/leaves/validate', {
        leaveVariationId: selectedVariationId,
        leaveStart: effectiveStart,
        leaveEnd: effectiveEnd,
        isHourly,
        afternoonExit: !isHourly && afternoonExit,
        afternoonEntry: !isHourly && afternoonEntry,
        tz,
        userNotes,
      });
      if (res.data?.status === false) {
        // Validation failed — interceptor already showed toast
        setValidating(false);
        return;
      }
      setValidationData(res.data?.data);
      setStep(3);
    } catch {
      // error handled by interceptor
    }
    setValidating(false);
  }, [selectedVariationId, selectedVariation, leaveStart, leaveEnd, afternoonExit, afternoonEntry, userNotes]);

  const handleCreate = useCallback(async () => {
    if (!validationData) return;
    // Çıkmaz sokak kapısı: belge zorunlu türde (Babalık/Evlilik/Ölüm/Doğum vb.)
    // ek olmadan sunucuya hiç gidilmez — buton zaten devre dışı bırakılır
    // (aşağıdaki JSX'te disabled={...}), bu ikinci bir güvence katmanı.
    if (validationData.requiresDocument && !documentFile) return;

    setSaving(true);

    let documents;
    if (documentFile) {
      try {
        // purpose=leaveDocument: backend StoragePurposes.Private kümesinde
        // kayıtlı özel kova amacı (Task 5 düzeltme turu 2) — başka bir amaç
        // kullanılırsa kova/ihraç kapısı 400 ile reddeder.
        const mediaUrl = await presignAndUploadFile(documentFile, 'leaveDocument');
        documents = [{ name: documentFile.name, mediaUrl }];
      } catch (err) {
        // Ham R2 adımı interceptor'dan geçmez; yalnız o bacağın hatası burada
        // gösterilir (ProfilePage.handlePhotoSelected ile aynı sözleşme).
        if (err?.isRawUploadError) setDocumentError(t('leave.documentUploadFailed'));
        setSaving(false);
        return;
      }
    }

    try {
      await api.post('/v1/leaves', {
        leaveVariationId: validationData.leaveVariationId,
        leaveStart: validationData.leaveStart,
        leaveEnd: validationData.leaveEnd,
        isHourly: validationData.isHourly,
        // Sunucu Create'te aynı kural gövdesini (ValidateCore) yeniden
        // çalıştırıp maliyeti/ücretliliği KENDİSİ hesaplıyor ve cost/isPaid/
        // currentApprover* alanlarını YOK SAYAR — ama deploy sıralaması
        // garantili değil (bu workflow yalnız backend'i deploy eder; yeni
        // frontend eski backend'e konuşabilir). Eski sunucu bu alanları hâlâ
        // istemciden okuyor, o yüzden GÖNDERİLMEYE DEVAM EDİLİR (review turu 1,
        // Critical — C-1). afternoonExit/afternoonEntry EKLENDİ: gönderilmezse
        // sunucu bunları false varsayar ve validate adımında gösterilenden
        // FARKLI (daha yüksek) bir maliyet yazar.
        cost: validationData.cost,
        isPaid: validationData.isPaid,
        currentApprover: validationData.currentApprover,
        currentApproverType: validationData.currentApproverType,
        afternoonExit: validationData.afternoonExit || false,
        afternoonEntry: validationData.afternoonEntry || false,
        tz: validationData.tz,
        userNotes,
        documents,
      });
      setDialogOpen(false);
      refresh();
    } catch {
      // error handled by interceptor
    }
    setSaving(false);
  }, [validationData, userNotes, documentFile, refresh, t]);

  const handleDetails = useCallback((row) => {
    history.push(`/leaves/${row.id}`);
  }, [history]);

  const handleDownloadForm = useCallback(async (row) => {
    try {
      const res = await api.get(`/v1/leaves/${row.id}/form`, { responseType: 'blob' });
      // Guard against the server returning JSON/HTML wrapped as a blob — only
      // save when the content is actually a PDF, otherwise the user ends up
      // with a corrupt file.
      const contentType = res.data?.type || res.headers?.['content-type'] || '';
      if (!contentType.includes('pdf')) return;
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `izin-formu-${row.id.slice(0, 8).toUpperCase()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      // interceptor handles error toast
    }
  }, []);

  // İzin mutabakat formu indirme — kayıt yoksa backend GetOrCreateAsync ile
  // KENDİSİ açar/üretir (bkz. leaveReconciliationService yorumu), bu yüzden
  // rozeti başarılı indirmeden SONRA tazeleriz (draft/yok → generated).
  // Aynı blob/content-type deseni handleDownloadForm ile BİREBİR (yukarı bkz.).
  const handleDownloadReconciliation = useCallback(async () => {
    setDownloadingReconciliation(true);
    try {
      const res = await downloadMyReconciliationPdf(reconciliationYear);
      const contentType = res.data?.type || res.headers?.['content-type'] || '';
      if (contentType.includes('pdf')) {
        const url = URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `izin-mutabakat-${reconciliationYear}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        const res2 = await getMyReconciliation(reconciliationYear);
        setReconciliationStatus(res2.data?.status ? (res2.data?.data?.status || null) : null);
      }
    } catch {
      // interceptor handles error toast
    }
    setDownloadingReconciliation(false);
  }, [reconciliationYear]);

  // Bulgu G — İNDİRMEDEN AYRI eylem. İndirme (yukarıda) donmuş dökümü basar ve
  // onu DEĞİŞTİRMEZ; bu düğme dökümü yeniden hesaplar. Gerekçesi: form içinde
  // bulunulan yıl için de üretilebiliyor — Mart'ta bir kez indiren çalışan, yıl
  // sonu belgesini Mart verisine çiviliyordu ve geri dönüş yolu YOKTU
  // (teslim geri alma `Dokum`u korur). `delivered` bir kayıtta backend reddeder;
  // düğme de o durumda gösterilmez.
  const handleRegenerateReconciliation = useCallback(async () => {
    setRegeneratingReconciliation(true);
    try {
      const res = await regenerateMyReconciliation(reconciliationYear);
      const contentType = res.data?.type || res.headers?.['content-type'] || '';
      if (contentType.includes('pdf')) {
        const url = URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `izin-mutabakat-${reconciliationYear}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        const res2 = await getMyReconciliation(reconciliationYear);
        setReconciliationStatus(res2.data?.status ? (res2.data?.data?.status || null) : null);
      }
    } catch {
      // interceptor handles error toast
    }
    setRegeneratingReconciliation(false);
    setRegenerateConfirm(false);
  }, [reconciliationYear]);

  const actionOptions = useMemo(() => [
    {
      type: 'cancel', label: t('leave.cancel'), onClick: handleCancel,
      disabled: (row) => row.status !== 'waiting',
    },
    {
      type: 'download', label: t('leave.downloadForm'), onClick: handleDownloadForm,
      disabled: (row) => row.status !== 'approved' || !row.isPaid,
    },
    { type: 'divider' },
    { type: 'details', label: t('leave.detail'), onClick: handleDetails },
  ], [t, handleCancel, handleDownloadForm, handleDetails]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={regenerateConfirm}
        title={t('leaveReconciliation.regenerateTitle')}
        message={t('leaveReconciliation.myRegenerateMessage', { year: reconciliationYear })}
        confirmText={t('leaveReconciliation.regenerate')}
        onConfirm={handleRegenerateReconciliation}
        onCancel={() => setRegenerateConfirm(false)}
        loading={regeneratingReconciliation}
      />

      <ConfirmDialog
        isOpen={cancelConfirm.isOpen}
        title={t('leave.cancelTitle')}
        message={t('leave.cancelMessage')}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelConfirm({ isOpen: false, id: null })}
        confirmText={t('leave.cancel')}
        loading={canceling}
      />

      {dialogOpen && (
        <Overlay onClick={() => setDialogOpen(false)}>
          <DialogBox onClick={(e) => e.stopPropagation()}>
            <DialogTitle>{t('leave.new')}</DialogTitle>

            <StepIndicator>
              <StepDot $active={step >= 1} />
              <StepDot $active={step >= 2} />
              <StepDot $active={step >= 3} />
            </StepIndicator>

            {step === 1 && (
              <>
                <VariationList>
                  {variations.map((v) => (
                    <VariationItem
                      key={v.id}
                      data-testid="variation-item"
                      $selected={selectedVariationId === v.id}
                      onClick={() => {
                        // Review turu 1 — Important: tür değişince önceki
                        // seçimden kalan belge (ör. Babalık İzni için yüklenen
                        // doğum belgesi) yeni türe SESSİZCE taşınmasın —
                        // belge-zorunlu olmayan bir türde "documents" alanına
                        // başka bir izne ait kişisel/tıbbi belge sızardı.
                        setSelectedVariationId(v.id);
                        setDocumentFile(null);
                        setDocumentError(null);
                      }}
                    >
                      <VariationName>{v.name}</VariationName>
                      {v.description && <VariationDesc>{v.description}</VariationDesc>}
                    </VariationItem>
                  ))}
                </VariationList>
                <DialogActions>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
                  <Button onClick={() => setStep(2)} disabled={!selectedVariationId}>
                    {t('leave.next')}
                  </Button>
                </DialogActions>
              </>
            )}

            {step === 2 && (
              <>
                <FormGrid>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <DateTimePicker
                        label={t('leave.leaveStart')}
                        value={leaveStart}
                        onChange={setLeaveStart}
                        mode={selectedVariation?.isHourly ? 'datetime' : 'date'}
                        required
                      />
                    </div>
                    {!selectedVariation?.isHourly && (
                      <CheckboxInput
                        label={t('leave.afternoonExit')}
                        checked={afternoonExit}
                        onChange={(val) => setAfternoonExit(val)}
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <DateTimePicker
                        label={selectedVariation?.isHourly ? t('leave.leaveEnd') : t('leave.returnDate')}
                        value={leaveEnd}
                        onChange={setLeaveEnd}
                        mode={selectedVariation?.isHourly ? 'datetime' : 'date'}
                        required
                      />
                    </div>
                    {!selectedVariation?.isHourly && (
                      <CheckboxInput
                        label={t('leave.afternoonEntry')}
                        checked={afternoonEntry}
                        onChange={(val) => setAfternoonEntry(val)}
                      />
                    )}
                  </div>
                  <TextInput
                    label={t('leave.userNotes')}
                    type="textarea"
                    value={userNotes}
                    onChange={(e) => setUserNotes(e.target.value)}
                    rows={3}
                  />
                </FormGrid>
                <DialogActions>
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Aynı sızıntı kapısı — adım 1'e her dönüşte belge
                      // sıfırlanır (kullanıcı burada türü DEĞİŞTİREBİLİR).
                      setStep(1);
                      setDocumentFile(null);
                      setDocumentError(null);
                    }}
                  >
                    {t('leave.back')}
                  </Button>
                  <Button onClick={handleStep2Next} disabled={validating || !leaveStart || !leaveEnd}>
                    {validating ? '...' : t('leave.next')}
                  </Button>
                </DialogActions>
              </>
            )}

            {step === 3 && (
              <>
                <ValidationResult>
                  <ValidationRow>
                    <ValidationLabel>{t('leave.leaveType')}</ValidationLabel>
                    <span>{selectedVariation?.name}</span>
                  </ValidationRow>
                  <ValidationRow>
                    <ValidationLabel>{t('leave.leaveStart')}</ValidationLabel>
                    <span>{formatDate(validationData?.leaveStart, i18n.language)}{afternoonExit ? ` (${t('leave.afternoonExit')})` : ''}</span>
                  </ValidationRow>
                  <ValidationRow>
                    <ValidationLabel>{validationData?.isHourly ? t('leave.leaveEnd') : t('leave.returnDate')}</ValidationLabel>
                    <span>{formatDate(validationData?.leaveEnd, i18n.language)}{afternoonEntry ? ` (${t('leave.afternoonEntry')})` : ''}</span>
                  </ValidationRow>
                  {validationData?.cost !== undefined && (
                    <ValidationRow>
                      <ValidationLabel>{t('leave.cost')}</ValidationLabel>
                      <span>{validationData.cost}</span>
                    </ValidationRow>
                  )}
                  {validationData?.currentApproverName && (
                    <ValidationRow>
                      <ValidationLabel>{t('leave.approver')}</ValidationLabel>
                      <span>{validationData.currentApproverName}</span>
                    </ValidationRow>
                  )}
                </ValidationResult>

                {selectedVariation?.balanceScope && selectedVariation.balanceScope !== 'annual' && (
                  <InfoNote>{t('leave.notDeductedFromAnnual')}</InfoNote>
                )}

                {validationData?.requiresDocument && (
                  <DocumentUploadWrap>
                    <PictureUpload
                      label={t('leave.document')}
                      required
                      value={documentFile}
                      onChange={(file) => { setDocumentFile(file); setDocumentError(null); }}
                      accept="image/*,.pdf"
                      validTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']}
                      maxSize={10}
                      error={documentError || (!documentFile ? t('responseErrors.leave.documentRequired') : null)}
                    />
                  </DocumentUploadWrap>
                )}

                <DialogActions>
                  <Button variant="outline" onClick={() => setStep(2)}>{t('leave.back')}</Button>
                  <Button
                    onClick={handleCreate}
                    disabled={saving || (validationData?.requiresDocument && !documentFile)}
                  >
                    {saving ? '...' : t('leave.create')}
                  </Button>
                </DialogActions>
              </>
            )}
          </DialogBox>
        </Overlay>
      )}

      {useMemo(() => (
        <DataTable
          title={t('leave.myLeaves')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={true}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
          toolbarActions={(
            <ReconciliationToolbar>
              <ReconciliationYearBox>
                <GenericSelect
                  label={t('leaveReconciliation.year')}
                  size="small"
                  clearable={false}
                  options={RECONCILIATION_YEAR_OPTIONS}
                  value={reconciliationYear}
                  onChange={setReconciliationYear}
                />
              </ReconciliationYearBox>
              <Button
                variant="outline"
                onClick={handleDownloadReconciliation}
                disabled={downloadingReconciliation}
              >
                <Download size={14} /> {t('leaveReconciliation.myFormButton')}
              </Button>
              {reconciliationStatus === 'generated' && (
                <Button
                  variant="outline"
                  onClick={() => setRegenerateConfirm(true)}
                  disabled={regeneratingReconciliation}
                >
                  <RefreshCw size={14} /> {t('leaveReconciliation.regenerate')}
                </Button>
              )}
              <ReconciliationBadge $delivered={reconciliationStatus === 'delivered'}>
                {reconciliationStatus === 'delivered'
                  ? t('leaveReconciliation.myDelivered')
                  : t('leaveReconciliation.myNotDelivered')}
              </ReconciliationBadge>
            </ReconciliationToolbar>
          )}
        />
      ), [
        t, fetching, columns, data, pagination,
        handleNewButtonClick, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
        reconciliationYear, reconciliationStatus, downloadingReconciliation, handleDownloadReconciliation,
        regeneratingReconciliation,
      ])}
    </Container>
  );
}
